import { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface Round {
  id: string;
  round_number: number;
  name: string;
}

interface GroupSummary {
  group_number: number;
  round_id: string;
  round_number: number;
  total_holes: number;
  draft_holes: number;
  submitted_holes: number;
  verified_holes: number;
  disputed_holes: number;
  players: { player_id: string; name: string }[];
}

interface Props {
  tournamentId: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-600' },
  submitted: { label: 'Submitted', color: 'bg-amber-100 text-amber-700' },
  verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700' },
  disputed: { label: 'Disputed', color: 'bg-red-100 text-red-700' },
};

export default function ScoreVerificationPanel({ tournamentId }: Props) {
  const [rounds, setRounds] = useState<Round[]>([]);
  const [selectedRoundId, setSelectedRoundId] = useState('');
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState<string | null>(null);

  useEffect(() => {
    loadRounds();
  }, [tournamentId]);

  useEffect(() => {
    if (selectedRoundId) loadGroups(selectedRoundId);
  }, [selectedRoundId]);

  const loadRounds = async () => {
    const { data } = await supabase
      .from('tournament_rounds')
      .select('id, round_number, name')
      .eq('tournament_id', tournamentId)
      .order('round_number');
    const list = data ?? [];
    setRounds(list);
    if (list.length > 0) setSelectedRoundId(list[0].id);
    else setLoading(false);
  };

  const loadGroups = async (roundId: string) => {
    setLoading(true);
    try {
      const round = rounds.find(r => r.id === roundId);
      if (!round) return;

      const [{ data: pairings }, { data: scores }] = await Promise.all([
        supabase
          .from('player_pairings')
          .select('group_number, player_id')
          .eq('round_id', roundId)
          .order('group_number'),
        supabase
          .from('hole_scores')
          .select('player_id, hole, score_status, group_number:hole')
          .eq('tournament_id', tournamentId)
          .eq('round', round.round_number),
      ]);

      // Build player name map
      const playerIds = [...new Set((pairings ?? []).map(p => p.player_id))];
      const playerMap = new Map<string, string>();
      if (playerIds.length > 0) {
        const { data: players } = await supabase
          .from('players')
          .select('id, first_name, last_name')
          .in('id', playerIds);
        (players ?? []).forEach(p => playerMap.set(p.id, `${p.first_name} ${p.last_name}`));
      }

      // Group pairings
      const groupPlayerMap = new Map<number, { player_id: string; name: string }[]>();
      for (const p of pairings ?? []) {
        if (!groupPlayerMap.has(p.group_number)) groupPlayerMap.set(p.group_number, []);
        groupPlayerMap.get(p.group_number)!.push({
          player_id: p.player_id,
          name: playerMap.get(p.player_id) ?? '—',
        });
      }

      // Aggregate score statuses per group
      // We need to get scores with their group by joining pairings
      const pairingGroupMap = new Map<string, number>();
      for (const p of pairings ?? []) pairingGroupMap.set(p.player_id, p.group_number);

      const groupScoreStats = new Map<number, { draft: number; submitted: number; verified: number; disputed: number; total: number }>();
      for (const [gn] of groupPlayerMap) {
        groupScoreStats.set(gn, { draft: 0, submitted: 0, verified: 0, disputed: 0, total: 0 });
      }

      for (const s of scores ?? []) {
        const gn = pairingGroupMap.get(s.player_id);
        if (gn === undefined) continue;
        const stat = groupScoreStats.get(gn);
        if (!stat) continue;
        stat.total++;
        const status = (s.score_status as string) ?? 'draft';
        if (status === 'submitted') stat.submitted++;
        else if (status === 'verified') stat.verified++;
        else if (status === 'disputed') stat.disputed++;
        else stat.draft++;
      }

      const summaries: GroupSummary[] = Array.from(groupPlayerMap.entries())
        .sort(([a], [b]) => a - b)
        .map(([gn, players]) => {
          const stat = groupScoreStats.get(gn) ?? { draft: 0, submitted: 0, verified: 0, disputed: 0, total: 0 };
          return {
            group_number: gn,
            round_id: roundId,
            round_number: round.round_number,
            total_holes: stat.total,
            draft_holes: stat.draft,
            submitted_holes: stat.submitted,
            verified_holes: stat.verified,
            disputed_holes: stat.disputed,
            players,
          };
        });

      setGroups(summaries);
    } catch {
      toast.error('Failed to load verification data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyGroup = async (group: GroupSummary) => {
    const key = `${group.round_id}-${group.group_number}`;
    setVerifying(key);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const round = rounds.find(r => r.id === group.round_id);
      if (!round) return;

      // Get player IDs in this group
      const playerIds = group.players.map(p => p.player_id);

      const { error } = await supabase
        .from('hole_scores')
        .update({
          score_status: 'verified',
          verified_at: new Date().toISOString(),
          verified_by: user?.id ?? null,
        })
        .eq('tournament_id', tournamentId)
        .eq('round', round.round_number)
        .in('player_id', playerIds)
        .in('score_status', ['submitted', 'draft']);

      if (error) throw error;
      toast.success(`Group ${group.group_number} scores verified`);
      await loadGroups(group.round_id);
    } catch {
      toast.error('Failed to verify scores');
    } finally {
      setVerifying(null);
    }
  };

  const handleDisputeGroup = async (group: GroupSummary) => {
    const key = `${group.round_id}-${group.group_number}`;
    setVerifying(key);
    try {
      const round = rounds.find(r => r.id === group.round_id);
      if (!round) return;
      const playerIds = group.players.map(p => p.player_id);

      const { error } = await supabase
        .from('hole_scores')
        .update({ score_status: 'disputed' })
        .eq('tournament_id', tournamentId)
        .eq('round', round.round_number)
        .in('player_id', playerIds)
        .neq('score_status', 'verified');

      if (error) throw error;
      toast.success(`Group ${group.group_number} flagged as disputed`);
      await loadGroups(group.round_id);
    } catch {
      toast.error('Failed to flag dispute');
    } finally {
      setVerifying(null);
    }
  };

  const groupStatus = (g: GroupSummary) => {
    if (g.disputed_holes > 0) return 'disputed';
    if (g.total_holes === 0) return 'pending';
    if (g.verified_holes === g.total_holes && g.total_holes > 0) return 'verified';
    if (g.submitted_holes > 0) return 'submitted';
    if (g.draft_holes > 0) return 'draft';
    return 'pending';
  };

  const summary = {
    total: groups.length,
    verified: groups.filter(g => groupStatus(g) === 'verified').length,
    submitted: groups.filter(g => groupStatus(g) === 'submitted').length,
    disputed: groups.filter(g => groupStatus(g) === 'disputed').length,
    pending: groups.filter(g => groupStatus(g) === 'pending').length,
  };

  return (
    <div className="space-y-4">
      {/* Round selector */}
      {rounds.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {rounds.map(r => (
            <button
              key={r.id}
              onClick={() => setSelectedRoundId(r.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                selectedRoundId === r.id
                  ? 'bg-golf-green text-white border-golf-green'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-golf-green'
              }`}
            >
              {r.name}
            </button>
          ))}
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total', value: summary.total, color: 'text-gray-700' },
          { label: 'Verified', value: summary.verified, color: 'text-emerald-600' },
          { label: 'Awaiting', value: summary.submitted + summary.draft, color: 'text-amber-600' },
          { label: 'Disputed', value: summary.disputed, color: 'text-red-600' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-100 rounded-xl p-3 text-center">
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-golf-green border-t-transparent rounded-full animate-spin" />
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center py-12">
          <UserGroupIcon className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">No groups with scores yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map(group => {
            const status = groupStatus(group);
            const key = `${group.round_id}-${group.group_number}`;
            const isVerifying = verifying === key;
            const statusInfo = STATUS_LABELS[status] ?? STATUS_LABELS.draft;
            const canVerify = group.total_holes > 0 && status !== 'verified';

            return (
              <div
                key={key}
                className={`bg-white border rounded-xl p-4 ${
                  status === 'disputed' ? 'border-red-200' : status === 'verified' ? 'border-emerald-200' : 'border-gray-100'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    status === 'verified' ? 'bg-emerald-100' : status === 'disputed' ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    {status === 'verified'
                      ? <ShieldCheckIcon className="w-5 h-5 text-emerald-600" />
                      : status === 'disputed'
                      ? <FlagIcon className="w-5 h-5 text-red-500" />
                      : <span className="text-sm font-bold text-gray-600">{group.group_number}</span>
                    }
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-golf-gray text-sm">Group {group.group_number}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 truncate">
                      {group.players.map(p => p.name).join(', ')}
                    </p>
                    {group.total_holes > 0 && (
                      <div className="flex items-center gap-3 mt-1.5">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${(group.verified_holes / group.total_holes) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {group.verified_holes}/{group.total_holes} holes verified
                        </span>
                      </div>
                    )}
                    {group.total_holes === 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        <ClockIcon className="w-3 h-3 text-gray-300" />
                        <span className="text-xs text-gray-400">No scores entered yet</span>
                      </div>
                    )}
                  </div>

                  {canVerify && (
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => handleVerifyGroup(group)}
                        disabled={isVerifying}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                      >
                        {isVerifying ? (
                          <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />
                        ) : (
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                        )}
                        Verify
                      </button>
                      {status !== 'disputed' && (
                        <button
                          onClick={() => handleDisputeGroup(group)}
                          disabled={isVerifying}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 border border-red-100"
                        >
                          <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                          Dispute
                        </button>
                      )}
                    </div>
                  )}

                  {status === 'verified' && (
                    <div className="flex-shrink-0">
                      <ShieldCheckIcon className="w-5 h-5 text-emerald-500" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
